import { TimeSeriesChartBuilder } from '@/composables/charts/influx-charts/chart-builders/TimeSeriesChartBuilder'
import { AbstractSerializedDatabaseEntryChart } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import Highcharts from 'highcharts'
import { getCurrentTimezone } from '@/composables/datetime'
import vuetify from '@/plugins/vuetify'
import { useSettingsStore } from '@/store/settings'
import { DocumentQuerySelect } from '@/types/Query'

export abstract class AbstractDatabaseEntryChartBuilder extends TimeSeriesChartBuilder {
  protected groupByDimensions?: string[]
  protected additionalFilters?: DocumentQuerySelect[]
  protected transientAdditionalFilters?: DocumentQuerySelect[]
  protected transientGroupByDimensions?: string[]

  protected constructor(serialized: AbstractSerializedDatabaseEntryChart) {
    super(serialized)
    this.groupByDimensions = serialized.groupByDimensions
    this.additionalFilters = serialized.additionalFilters
  }
  serialize(): AbstractSerializedDatabaseEntryChart {
    return {
      ...super.serialize(),
      groupByDimensions: this.groupByDimensions,
      additionalFilters: this.additionalFilters,
    }
  }
  buildOptions(): void {
    const theme = vuetify.theme.themes.value[useSettingsStore().theme]
    const chartOptions: Highcharts.ChartOptions = {
      type: this.getRenderChartType(),
      style: {
        fontFamily: 'roboto',
      },
      zooming: {
        type: 'xy',
      },
      backgroundColor: 'transparent',
    }
    const noDataOptions: Highcharts.NoDataOptions = {
      style: { fontWeight: 'bold', fontSize: '20px' },
    }
    const loadingOptions: Highcharts.LoadingOptions = {
      style: {
        backgroundColor: 'transparent',
        fontSize: '20px',
      },
      showDuration: 50,
      hideDuration: 500,
    }

    const creditOptions: Highcharts.CreditsOptions = {
      text: 'vetvise.com',
      href: 'https://vetvise.com',
    }
    const timeOptions: Highcharts.TimeOptions = {
      timezone: getCurrentTimezone(),
    }

    const titleOptions: Highcharts.TitleOptions = {
      text: this.title,
      style: { color: theme.colors.onSurface },
    }
    const subtitleOptions: Highcharts.SubtitleOptions = {
      text: this.subtitle,
      style: { color: theme.colors.onSurface },
    }
    const compiledTimeSetup = this._timeSetup.compile()
    const xAxisOptions: Highcharts.XAxisOptions = {
      type: 'datetime',
      min: compiledTimeSetup.start,
      max: compiledTimeSetup.end,
      labels: {
        style: {
          color: theme.colors.onSurface,
        },
      },
    }

    const exportingOptions: Highcharts.ExportingOptions = {
      enabled: true,
      fallbackToExportServer: false,
      sourceWidth: 3000,
      sourceHeight: 500,
    }
    const tooltipOptions: Highcharts.TooltipOptions = {
      distance: 25,
      hideDelay: 100,
      pointFormatter: function () {
        return `<span style='color: ${this.color}'>●</span> ${this.series.name}: <b>${this.y}</b><br>`
      },
    }

    const yAxisOptions: Highcharts.YAxisOptions = {
      labels: {
        style: {
          color: theme.colors.onSurface,
        },
      },
    }
    const legendOptions: Highcharts.LegendOptions = {
      itemStyle: {
        color: theme.colors.onSurface,
      },
    }
    this.options = {
      chart: chartOptions,
      title: titleOptions,
      subtitle: subtitleOptions,
      xAxis: xAxisOptions,
      noData: noDataOptions,
      loading: loadingOptions,
      credits: creditOptions,
      time: timeOptions,
      plotOptions: this.plotOptions,
      exporting: exportingOptions,
      tooltip: tooltipOptions,
      yAxis: yAxisOptions,
      legend: legendOptions,
    }
  }
  getRenderChartType(): string {
    return 'column'
  }
}
